/**
 * Client-side security utilities for ScoutMind application
 * Provides encryption for sensitive data transmission
 */

// Simple encryption using Web Crypto API for modern browsers
export class ClientSecurity {
  private static readonly ALGORITHM = 'AES-GCM';
  private static readonly KEY_LENGTH = 256;
  
  /**
   * Generate a cryptographic key for encrypting sensitive data
   */
  private static async generateKey(): Promise<CryptoKey> {
    return await window.crypto.subtle.generateKey(
      {
        name: this.ALGORITHM,
        length: this.KEY_LENGTH,
      },
      true,
      ['encrypt', 'decrypt']
    );
  }
  
  /**
   * Encrypt sensitive data for transmission
   */
  static async encryptData(data: string): Promise<{ encryptedData: string; keyData: string; iv: string }> {
    try {
      // Generate a new key for each encryption
      const key = await this.generateKey();
      const keyData = await window.crypto.subtle.exportKey('raw', key);
      
      // Generate initialization vector
      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      
      // Encrypt the data
      const encoder = new TextEncoder();
      const encodedData = encoder.encode(data);
      
      const encryptedBuffer = await window.crypto.subtle.encrypt(
        {
          name: this.ALGORITHM,
          iv: iv,
        },
        key,
        encodedData
      );
      
      // Convert to base64 for transmission
      const encryptedData = btoa(String.fromCharCode(...new Uint8Array(encryptedBuffer)));
      const keyDataB64 = btoa(String.fromCharCode(...new Uint8Array(keyData)));
      const ivB64 = btoa(String.fromCharCode(...new Uint8Array(iv)));
      
      return {
        encryptedData,
        keyData: keyDataB64,
        iv: ivB64
      };
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }
  
  /**
   * Create a hash of the request for integrity verification
   */
  static async createRequestHash(data: any): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const dataString = JSON.stringify(data);
      const dataBuffer = encoder.encode(dataString);
      
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', dataBuffer);
      return btoa(String.fromCharCode(...new Uint8Array(hashBuffer)));
    } catch (error) {
      console.error('Hash creation failed:', error);
      throw new Error('Failed to create request hash');
    }
  }
  
  /**
   * Check if the browser supports required crypto features
   */
  static isSupported(): boolean {
    return !!(
      window.crypto &&
      window.crypto.subtle &&
      typeof window.crypto.subtle.generateKey === 'function' &&
      typeof window.crypto.subtle.encrypt === 'function' &&
      typeof window.crypto.subtle.digest === 'function'
    );
  }
}

/**
 * Enhanced login credentials with security features
 */
export interface SecureLoginCredentials {
  username: string;
  password: string;
  timestamp: number;
  nonce: string;
}

/**
 * Create secure login payload with additional security measures
 */
export const createSecureLoginPayload = async (username: string, password: string) => {
  // Check if crypto is supported
  if (!ClientSecurity.isSupported()) {
    console.warn('Crypto API not supported, falling back to plain text (development only)');
    return {
      username,
      password,
      encrypted: false,
      timestamp: Date.now(),
      nonce: Math.random().toString(36).substring(2, 15)
    };
  }
  
  try {
    // Create secure credentials object
    const credentials: SecureLoginCredentials = {
      username,
      password,
      timestamp: Date.now(),
      nonce: Math.random().toString(36).substring(2, 15)
    };
    
    // Encrypt the credentials
    const credentialsString = JSON.stringify(credentials);
    const encrypted = await ClientSecurity.encryptData(credentialsString);
    
    // Create request hash for integrity
    const requestHash = await ClientSecurity.createRequestHash(encrypted);
    
    return {
      encryptedCredentials: encrypted.encryptedData,
      keyData: encrypted.keyData,
      iv: encrypted.iv,
      hash: requestHash,
      encrypted: true,
      // Include fallback credentials in case decryption fails on backend
      username,
      password
    };
  } catch (error) {
    console.error('Failed to create secure payload:', error);
    // Fallback to plain text with warning
    console.warn('Falling back to plain text transmission (not recommended)');
    return {
      username,
      password,
      encrypted: false,
      timestamp: Date.now(),
      nonce: Math.random().toString(36).substring(2, 15)
    };
  }
};